import { useLocation } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
    BarChart3,
    Calendar,
    CalendarDays,
    Clock,
    Database,
    History,
    Loader2,
    SkipBack,
    SkipForward,
    Sparkles,
    TrendingUp,
    Zap,
} from 'lucide-react'
import type React from 'react'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useShallow } from 'zustand/react/shallow'
import { AnalysisMode, IndexSymbol, MARKET_HOURS, SUPPORTED_SYMBOLS, TimeGap } from '../../config/constants'
import { useActiveHistory } from '../../hooks/useDataBus'
import { toIST } from '../../lib/data-utils'
import { cn } from '../../lib/utils'
import { useMarketStore } from '../../services/store'
import { useSettingsStore } from '../../stores/settingsStore'
import { AiInsightsModal } from '../ai/AiInsightsModal'
import { TimeRangeSlicer } from '../analysis/TimeRangeSlicer'
import { AtmRangePicker } from '../ui/AtmRangePicker'
import { BaseTimePicker } from '../ui/BaseTimePicker'
import { HistoricalDatePicker } from '../ui/HistoricalDatePicker'
import { MaterialSelect, type MaterialSelectOption } from '../ui/MaterialSelect'
import { LiveTickerDisplay } from './LiveTickerDisplay'

const EMPTY_ARRAY: string[] = []

const MIN_TIME = MARKET_HOURS.START_MINUTES
const MAX_TIME = MARKET_HOURS.END_MINUTES

const timeToMinutes = (timeStr: string): number => {
    if (!timeStr || !timeStr.includes(':')) return MIN_TIME
    const parts = timeStr.split(':')
    const h = Number(parts[0] || 0)
    const m = Number(parts[1] || 0)
    return h * 60 + m
}

const minutesToTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60)
    const m = Math.floor(minutes % 60)
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

const gapToMinutes = (gap: TimeGap): number => {
    switch (gap) {
        case TimeGap.M1:
            return 1
        case TimeGap.M5:
            return 5
        case TimeGap.M10:
            return 10
        case TimeGap.M15:
            return 15
        case TimeGap.M30:
            return 30
        case TimeGap.H1:
            return 60
        case TimeGap.H2:
            return 120
        case TimeGap.H4:
            return 240
        default:
            return 5
    }
}

const normalizeDateStr = (d: string | null) => {
    if (!d) return ''
    let str = d
    if (str.includes('T')) str = str.split('T')[0] || str
    if (str.includes(' ')) str = str.split(' ')[0] || str
    // If YYYY-MM-DD, convert to DD-MM-YYYY
    const parts = str.split('-')
    if (parts.length === 3 && parts[0] && parts[0].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
    }
    return str
}

const getExpiryDteLabel = (dateStr: string): string => {
    if (!dateStr) return ''
    const parts = dateStr.split('-')
    if (parts.length !== 3) return ''
    const day = parseInt(parts[0] || '0', 10)
    const month = parseInt(parts[1] || '0', 10) - 1
    const year = parseInt(parts[2] || '0', 10)
    if (!day || month < 0 || !year) return ''

    const expDate = new Date(year, month, day)
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const diffMs = expDate.getTime() - today.getTime()
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Tomorrow'
    if (diffDays > 1) return `${diffDays}d left`
    if (diffDays < 0) return 'Past'
    return ''
}

export const HeaderControls: React.FC = () => {
    const settings = useSettingsStore(
        useShallow((state) => ({
            symbol: state.symbol,
            expiry: state.expiry,
            range: state.range,
            timeGap: state.timeGap,
            simulationTime: state.simulationTime,
            baseTime: state.baseTime,
            mode: state.mode,
            isLive: state.isLive,
            historicalDate: state.historicalDate,
            datasetDate: state.datasetDate,
            inspectionSource: state.inspectionSource,
            setSymbol: state.setSymbol,
            setExpiry: state.setExpiry,
            setRange: state.setRange,
            setTimeGap: state.setTimeGap,
            setSimulationTime: state.setSimulationTime,
            setBaseTime: state.setBaseTime,
            setMode: state.setMode,
            setIsLive: state.setIsLive,
            setHistoricalDate: state.setHistoricalDate,
            isSlicerSticky: state.isSlicerSticky,
        })),
    )

    // From MarketStore/Service
    const location = useLocation()
    const isAnalysisPage = location.pathname.includes('/analysis')
    const expiryList = useMarketStore((state) => state.expiryMap[settings.symbol] || EMPTY_ARRAY)
    const isExpiryLoading = expiryList.length === 0

    const [isAiOpen, setIsAiOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            const scrolled = window.scrollY > 90
            setIsScrolled((prev) => (prev !== scrolled ? scrolled : prev))
        }
        window.addEventListener('scroll', handleScroll, { passive: true })
        handleScroll()
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const historyData = useActiveHistory(settings.symbol)

    const availableTimes = useMemo(() => {
        if (!historyData || historyData.length === 0) return []

        return historyData
            .map((row: any) => {
                const timeVal = row.timestampIST || row.timestamp || row.time
                if (!timeVal) return null

                const istStr = toIST(timeVal)
                const timePart = istStr
                    .split(/T| /)[1]
                    ?.replace(/[+Z].*$/, '')
                    .substring(0, 5)
                return timePart || null
            })
            .filter(Boolean) as string[]
    }, [historyData])

    const effectiveTime = useMemo(() => {
        return settings.simulationTime
    }, [settings.simulationTime])

    const handleQuickJump = (direction: 'prev' | 'next') => {
        if (availableTimes.length === 0) return

        const currentMinutes = timeToMinutes(effectiveTime)
        const gapMinutes = gapToMinutes(settings.timeGap)

        const sortedTimes = [...new Set(availableTimes)].sort()
        const sortedMinutes = sortedTimes.map(timeToMinutes)

        if (direction === 'prev') {
            const targetMin = currentMinutes - gapMinutes
            const prevAvailable = sortedMinutes.filter((m) => m <= targetMin)
            if (prevAvailable.length > 0) {
                const closest = prevAvailable[prevAvailable.length - 1]
                if (closest !== undefined) {
                    settings.setSimulationTime(minutesToTime(closest))
                }
            }
        } else {
            const targetMin = currentMinutes + gapMinutes
            const nextAvailable = sortedMinutes.filter((m) => m >= targetMin)
            if (nextAvailable.length > 0) {
                const closest = nextAvailable[0]
                if (closest !== undefined) {
                    settings.setSimulationTime(minutesToTime(closest))
                }
            }
        }
    }

    const handleSymbolChange = useCallback(
        (newSymbolVal: string) => {
            const newSymbol = newSymbolVal as IndexSymbol
            if (newSymbol === settings.symbol) return

            // Immediately look up available expiries for the target symbol from store
            const targetExpiries = useMarketStore.getState().expiryMap[newSymbol] || []
            const nextExpiry = targetExpiries[0] || null

            // Atomic update in Zustand avoids secondary re-render waterfall
            startTransition(() => {
                useSettingsStore.setState({
                    symbol: newSymbol,
                    ...(nextExpiry ? { expiry: nextExpiry } : {}),
                })
            })
        },
        [settings.symbol],
    )

    const handleExpiryChange = useCallback(
        (newExpiry: string) => {
            if (newExpiry === settings.expiry) return
            startTransition(() => {
                settings.setExpiry(newExpiry)
            })
        },
        [settings.expiry, settings.setExpiry],
    )

    const getSymbolLiveQuote = useCallback((sym: string) => {
        const marketState = useMarketStore.getState()
        const ltp = marketState.indexLtp[sym] || 0
        const meta = marketState.marketMeta[sym]
        const changePercent = meta?.dayChangePercent ?? meta?.dayChangeP ?? 0
        return { ltp, changePercent }
    }, [])

    const symbolOptions: MaterialSelectOption[] = useMemo(
        () => [
            {
                value: IndexSymbol.NIFTY,
                label: 'NIFTY',
                subtitle: 'Nifty 50 Index',
                badge: 'NSE',
                badgeColor: 'blue',
                tag: 'Step 50',
            },
            {
                value: IndexSymbol.BANKNIFTY,
                label: 'BANKNIFTY',
                subtitle: 'Bank Nifty Index',
                badge: 'NSE',
                badgeColor: 'purple',
                tag: 'Step 100',
            },
            {
                value: IndexSymbol.SENSEX,
                label: 'SENSEX',
                subtitle: 'BSE Sensex 30',
                badge: 'BSE',
                badgeColor: 'emerald',
                tag: 'Step 100',
            },
        ],
        [],
    )

    const expiryOptions: MaterialSelectOption[] = useMemo(() => {
        // Enforce Current and Next expiry dates (max 2) per user rules
        return expiryList.slice(0, 2).map((date, idx) => {
            const isCurrent = idx === 0
            const dte = getExpiryDteLabel(date)
            return {
                value: date,
                label: settings.inspectionSource === 'HistoricalSnapshot' ? `${date} (Hist)` : date,
                subtitle: isCurrent ? 'Active Current Cycle' : 'Next Cycle Rolling',
                badge: isCurrent ? 'Current' : 'Next',
                badgeColor: isCurrent ? 'emerald' : 'indigo',
                tag: dte || (isCurrent ? 'Near' : 'Next'),
            }
        })
    }, [expiryList, settings.inspectionSource])

    // Auto-select first expiry if current selection is invalid for the list
    useEffect(() => {
        if (expiryList.length > 0) {
            // If current expiry is empty OR not in the new list, select the first one.
            if (!settings.expiry || !expiryList.includes(settings.expiry)) {
                const firstExpiry = expiryList[0]
                if (firstExpiry) {
                    startTransition(() => {
                        settings.setExpiry(firstExpiry)
                    })
                }
            }
        }
    }, [expiryList, settings.expiry, settings.setExpiry])

    return (
        <div className="flex flex-col w-full transition-all duration-300 relative z-20 pointer-events-none mt-1 mb-2">
            <div className="w-full flex justify-center overflow-visible px-2 sm:px-4 md:px-6 py-1">
                {/* 🌟 FLOATING ALIGNED PILLS (NO BACKGROUND) 🌟 */}
                <motion.div
                    initial={{ y: -20, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    className="pointer-events-auto flex flex-row items-center w-full justify-center gap-y-2.5 gap-x-1.5 sm:gap-x-2 xl:gap-x-2.5 2xl:gap-x-3 flex-wrap xl:flex-nowrap"
                >
                    {/* SECTION 1: Symbol */}
                    <MaterialSelect
                        value={settings.symbol}
                        onChange={handleSymbolChange}
                        options={symbolOptions}
                        searchable={true}
                        searchPlaceholder="Search index or symbol..."
                        pinnedValues={[IndexSymbol.NIFTY, IndexSymbol.BANKNIFTY, IndexSymbol.SENSEX]}
                        headerTitle="SELECT MARKET INDEX"
                        getLiveQuote={getSymbolLiveQuote}
                        icon={<TrendingUp className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-indigo-500 shrink-0" />}
                        className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-1.5 lg:py-2 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 rounded-full transition-all w-auto min-w-[90px] lg:min-w-[110px] text-[12px] lg:text-[14px] font-bold premium-cell-mono text-slate-800 dark:text-slate-100 outline-none cursor-pointer border border-slate-200/90 dark:border-slate-700 shadow-xs hover:border-indigo-400 dark:hover:border-indigo-500/60 hover:shadow-sm shrink-0"
                        variant="custom"
                    />

                    {/* SECTION 2: Expiry Date */}
                    <MaterialSelect
                        value={settings.expiry || ''}
                        onChange={handleExpiryChange}
                        options={expiryOptions}
                        disabled={isExpiryLoading}
                        headerTitle="EXPIRY CONTRACT"
                        showTagInTrigger={true}
                        showQuickToggle={true}
                        icon={<Calendar className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-emerald-500 shrink-0" />}
                        className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-1.5 lg:py-2 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 rounded-full transition-all w-auto min-w-[140px] lg:min-w-[170px] text-[12px] lg:text-[14px] font-bold premium-cell-mono text-slate-800 dark:text-slate-100 outline-none cursor-pointer border border-slate-200/90 dark:border-slate-700 shadow-xs hover:border-emerald-400 dark:hover:border-emerald-500/60 hover:shadow-sm shrink-0"
                        variant="custom"
                    />

                    {/* SECTION 3: ATM Strike Range Picker */}
                    <AtmRangePicker
                        value={settings.range}
                        onChange={(val) => settings.setRange(val)}
                        min={1}
                        max={100}
                    />

                    {/* SECTION 4: Base Time Picker */}
                    <div className="flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full transition-colors border border-slate-200/80 dark:border-slate-700 shadow-sm cursor-pointer group w-auto shrink-0">
                        <Clock className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-slate-400 shrink-0 group-hover:text-slate-600 transition-colors" />
                        <BaseTimePicker
                            value={settings.baseTime || MARKET_HOURS.BASE_TIME_STR}
                            minTime={MARKET_HOURS.START_TIME_STR}
                            maxTime={MARKET_HOURS.END_TIME_STR}
                            onChange={(val: string) => settings.setBaseTime(val)}
                        />
                    </div>

                    {/* SECTION 5: Mode Toggle (OI / Volume) */}
                    <div className="flex items-center bg-slate-200/80 dark:bg-slate-800/80 p-0.5 lg:p-1 rounded-full border border-slate-300/80 dark:border-slate-700 shadow-inner shrink-0">
                        {[AnalysisMode.OI, AnalysisMode.VOLUME].map((m) => {
                            const isActive = settings.mode === m
                            return (
                                <button
                                    key={m}
                                    onClick={() => startTransition(() => settings.setMode(m))}
                                    className={`px-2.5 lg:px-3.5 py-0.5 lg:py-1 rounded-full text-[11px] lg:text-[12px] font-bold transition-all cursor-pointer ${
                                        isActive
                                            ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-sm font-black'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {m === AnalysisMode.VOLUME ? 'VOL' : m}
                                </button>
                            )
                        })}
                    </div>

                    {/* SECTION 6: Live / Historical Mode Toggle */}
                    <div className="flex items-center bg-slate-200/80 dark:bg-slate-800/80 p-0.5 lg:p-1 rounded-full border border-slate-300/80 dark:border-slate-700 shadow-inner shrink-0">
                        {[
                            { isLive: true, label: 'Live', icon: Zap },
                            { isLive: false, label: 'Hist', icon: History },
                        ].map(({ isLive, label, icon: Icon }) => {
                            const isActive = settings.isLive === isLive
                            return (
                                <button
                                    key={label}
                                    onClick={() => startTransition(() => settings.setIsLive(isLive))}
                                    className={`flex items-center gap-1 px-2.5 lg:px-3.5 py-0.5 lg:py-1 rounded-full text-[11px] lg:text-[12px] font-bold transition-all cursor-pointer ${
                                        isActive
                                            ? isLive
                                                ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm font-black'
                                                : 'bg-white dark:bg-slate-700 text-amber-600 dark:text-amber-400 shadow-sm font-black'
                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                                    }`}
                                >
                                    <Icon className="w-3 lg:w-3.5 h-3 lg:h-3.5" />
                                    <span>{label}</span>
                                </button>
                            )
                        })}
                    </div>

                    {/* SECTION 7: Status/Ticker */}
                    {!settings.isLive ? (
                        <>
                            {(() => {
                                const isDateMismatch =
                                    settings.datasetDate &&
                                    normalizeDateStr(settings.datasetDate) !== normalizeDateStr(settings.historicalDate)
                                return (
                                    <>
                                        <div
                                            className={`flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 rounded-full border shadow-sm transition-colors hover:opacity-90 group w-auto shrink-0 ${isDateMismatch ? 'bg-red-100 dark:bg-red-900/40 border-red-500/80 dark:border-red-500/80' : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200/60 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50'}`}
                                        >
                                            <CalendarDays
                                                className={`w-3.5 lg:w-4 h-3.5 lg:h-4 shrink-0 ${isDateMismatch ? 'text-red-600 dark:text-red-400 font-bold animate-pulse' : 'text-amber-600 dark:text-amber-500'}`}
                                            />
                                            <HistoricalDatePicker
                                                symbol={settings.symbol}
                                                currentDateStr={settings.historicalDate}
                                                onChange={(dateStr) =>
                                                    startTransition(() => settings.setHistoricalDate(dateStr))
                                                }
                                                className={`flex items-center outline-none bg-transparent border-none p-0 ${isDateMismatch ? 'text-red-700 dark:text-red-300 font-bold' : ''}`}
                                                hideIcon={true}
                                            />
                                        </div>

                                        {/* SECTION 8: Dataset Date (Parquet Source) */}
                                        {settings.datasetDate && (
                                            <div
                                                className={`flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 rounded-full border shadow-sm transition-colors group w-auto shrink-0 relative cursor-help ${isDateMismatch ? 'bg-red-100 dark:bg-red-900/40 border-red-500/80 dark:border-red-500/80' : 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200/60 dark:border-indigo-800'}`}
                                            >
                                                <Database
                                                    className={`w-3.5 lg:w-4 h-3.5 lg:h-4 shrink-0 ${isDateMismatch ? 'text-red-600 dark:text-red-400 font-bold animate-pulse' : 'text-indigo-500'}`}
                                                />
                                                <span
                                                    className={`text-[11px] lg:text-[12px] font-bold premium-cell-mono ${isDateMismatch ? 'text-red-700 dark:text-red-300' : 'text-indigo-700 dark:text-indigo-400'}`}
                                                >
                                                    {settings.datasetDate}
                                                </span>
                                                <div
                                                    className={`absolute hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 text-white text-[10px] rounded shadow-lg ${isDateMismatch ? 'bg-red-600 font-bold text-[11px]' : 'bg-slate-800'}`}
                                                >
                                                    {isDateMismatch
                                                        ? '⚠ DATES MISMATCH! Data loaded is for a different day!'
                                                        : 'Data Extracted From File'}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )
                            })()}
                        </>
                    ) : (
                        <div className="flex shrink-0">
                            <LiveTickerDisplay />
                        </div>
                    )}
                </motion.div>
            </div>

            <AiInsightsModal isOpen={isAiOpen} onClose={() => setIsAiOpen(false)} response={null} />

            {!settings.isLive && !isAnalysisPage && (
                <>
                    {/* Normal Inline Time Slicer Under Header (Always in standard position, never blocks navigation) */}
                    <div className="w-full relative z-20 bg-surfaceVariant/10 dark:bg-slate-900/30 border-b border-gray-100/50 dark:border-slate-800/50 pointer-events-auto">
                        <div className="w-full max-w-[1920px] mx-auto px-2 sm:px-4 lg:px-6 py-1.5 flex items-center justify-center">
                            <div className="w-full">
                                <TimeRangeSlicer
                                    symbol={settings.symbol}
                                    minMinutes={MIN_TIME}
                                    maxMinutes={MAX_TIME}
                                    viewMode="snapshot"
                                    onViewModeChange={() => {}}
                                    onChange={(_start, end) => {
                                        settings.setSimulationTime(end)
                                    }}
                                    availableTimes={availableTimes}
                                    hideModeToggle={true}
                                    forceFullDay={true}
                                    className="bg-white/95 dark:bg-slate-800/95 p-2 sm:p-2.5 rounded-2xl border border-slate-200/90 dark:border-slate-700/80 shadow-md w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Pinned Floating Time Slicer: Seamless Edge-to-Edge Bar (No empty white margins) */}
                    {settings.isSlicerSticky &&
                        isScrolled &&
                        typeof document !== 'undefined' &&
                        createPortal(
                            <div className="fixed top-0 left-0 right-0 z-[99999] bg-white/95 dark:bg-[#070b14]/95 backdrop-blur-2xl border-b border-slate-200/90 dark:border-slate-800/90 shadow-xl py-1.5 px-3 sm:px-6 pointer-events-auto animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="w-full max-w-[1920px] mx-auto">
                                    <TimeRangeSlicer
                                        symbol={settings.symbol}
                                        minMinutes={MIN_TIME}
                                        maxMinutes={MAX_TIME}
                                        viewMode="snapshot"
                                        onViewModeChange={() => {}}
                                        onChange={(_start, end) => {
                                            settings.setSimulationTime(end)
                                        }}
                                        availableTimes={availableTimes}
                                        hideModeToggle={true}
                                        forceFullDay={true}
                                        className="!bg-transparent !border-0 !shadow-none !p-0 !rounded-none w-full"
                                    />
                                </div>
                            </div>,
                            document.body,
                        )}
                </>
            )}
        </div>
    )
}
